import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DataType } from '@mode-switch/express';
import { DatePart } from '@mode-vegas/vegas';
import { ButtonComponent, IconComponent, trackByFactory } from '@mode/capra';
import { CompiledField } from '@mode/shared/util-switch';
import * as util from '../filter-modal-helpers';
import { FilterStep } from '../filter-modal.component';

export interface FilterTypeDetail {
  key: string;
  label: string;
  icon: string;
  step: FilterStep;
}

/**
 * This component displays the filter type picker.
 * It is displayed when the field type could support
 * more than one filter type. For example, a date
 * field filter could be represented as a range, individual
 * dates, year, month, etc.
 *
 * This component displays the appropriate options
 * and emits the selected choice if the user chooses
 * 'Next'.
 */
@Component({
  standalone: true,
  imports: [CommonModule, ButtonComponent, IconComponent],
  selector: 'mode-filter-type-selector',
  templateUrl: './filter-type-selector.component.html',
  styleUrls: ['./filter-type-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterTypeSelectorComponent implements OnInit {
  @Input() field!: CompiledField;
  @Output() canceled = new EventEmitter<void>();
  @Output() next = new EventEmitter<FilterTypeDetail>();

  FilterStepEnum = FilterStep;
  filterTypeList: Array<FilterTypeDetail> = [];
  trackByKey = trackByFactory<FilterTypeDetail>(({ key }) => key);
  selectedFilterType: FilterTypeDetail | undefined;

  ngOnInit() {
    // Build the filter types
    if (this.field.attrDataType === DataType.Name.Timestamp || this.field.attrDataType === DataType.Name.DateTime) {
      this.filterTypeList.push(
        this.makeFilterTypeItem(
          'relative_date_range',
          'Range of dates',
          'icon-field-date',
          FilterStep.RelativeDateRange
        )
      );

      this.filterTypeList.push(this.makeFilterTypeItem('individual_values', 'Individual dates'));
      // TODO: Separator
      this.filterTypeList.push(this.makeFilterTypeItem(DatePart.YEAR, 'Year'));
      this.filterTypeList.push(this.makeFilterTypeItem(DatePart.QUARTER, 'Quarter'));
      this.filterTypeList.push(this.makeFilterTypeItem(DatePart.MONTH, 'Month'));
      this.filterTypeList.push(this.makeFilterTypeItem(DatePart.DAY, 'Day of month'));
      this.filterTypeList.push(this.makeFilterTypeItem(DatePart.WEEK, 'Week number'));
      this.filterTypeList.push(this.makeFilterTypeItem(DatePart.WEEKDAY, 'Day of week'));
      this.filterTypeList.push(this.makeFilterTypeItem(DatePart.HOUR, 'Hour'));
      this.filterTypeList.push(this.makeFilterTypeItem(DatePart.MINUTE, 'Minute'));
      this.filterTypeList.push(this.makeFilterTypeItem(DatePart.SECOND, 'Second'));
    } else {
      this.filterTypeList.push(
        this.makeFilterTypeItem('range', 'Range of values', 'icon-field-numeric', FilterStep.Range)
      );
      this.filterTypeList.push(this.makeFilterTypeItem('individual_values', 'Individual values'));
    }

    if (util.supportsTopNFilter(this.field)) {
      this.filterTypeList.push(
        this.makeFilterTypeItem('top_by_field', 'Top/Bottom values', 'icon-field-numeric', FilterStep.TopByField)
      );
    }
    this.selectedFilterType = this.filterTypeList[0];
  }

  itemSelected(item: FilterTypeDetail) {
    this.selectedFilterType = item;
  }

  doCancel() {
    this.canceled.emit();
  }

  doNext() {
    this.next.emit(this.selectedFilterType);
  }

  makeFilterTypeItem(
    key: string,
    label: string,
    icon = 'icon-field-numeric',
    step: FilterStep = FilterStep.MultiValues
  ): FilterTypeDetail {
    return {
      key,
      label,
      icon,
      step,
    };
  }

  isDiscreteFilterStep(step: FilterStep) {
    switch (step) {
      case FilterStep.Range:
      case FilterStep.RelativeDateRange: {
        return false;
      }
      default: {
        return true;
      }
    }
  }
}
